import { Fragment, PropsWithChildren, useRef } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { classNamesOrEmpty } from '../../style/common'

export type ModalProps = PropsWithChildren & {
    isOpen: boolean,
    onOpenChange: (isOpen: boolean) => void,
    title?: string | undefined,
    key?: string | undefined,
    ignoreNavbarDodge?: boolean,
    onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void,
    classNames?: {
        modalTransitionContainer?: string | undefined,
        dialogPanel?: string | undefined,
        mainContainer?: string | undefined
    }
}

export default function Modal(props: ModalProps) {
    const cancelButtonRef = useRef(null);

    return (
        <Transition key={props.key} show={props.isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" initialFocus={cancelButtonRef} onClose={() => props.onOpenChange(false)} onKeyDown={props.onKeyDown}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className={`fixed inset-0 z-10 w-screen overflow-y-auto ${props.ignoreNavbarDodge ? "" : "lg:left-[144px]"}`}>
                    <div className={`flex min-h-full items-end justify-center p-2 text-center sm:items-center sm:p-0 ${classNamesOrEmpty(props.classNames?.modalTransitionContainer)}`}>
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className={`relative transform rounded-lg bg-secondary px-4 pb-4 pt-4 text-left shadow-xl transition-all sm:my-8 w-full sm:w-full sm:max-w-lg md:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl 3xl:max-w-6xl 4xl:max-w-7xl ${classNamesOrEmpty(props.classNames?.dialogPanel)}`}>
                                <div className="absolute right-0 top-0 pr-4 pt-4 sm:block">
                                    <button
                                        type="button"
                                        className="rounded-md hover:text-contrast-tertiary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        onClick={() => props.onOpenChange(false)}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="sm:flex sm:items-start">
                                    <div className={`mt-3 sm:mt-0 text-left min-w-full ${classNamesOrEmpty(props.classNames?.mainContainer)}`}>
                                        {
                                            props.title &&
                                            <DialogTitle as="h3" className="text-base font-semibold leading-6">
                                                {props.title}
                                            </DialogTitle>
                                        }
                                        {
                                            props.children &&
                                            props.children
                                        }
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}